import { Flex } from '@chakra-ui/react';
import { Container, Stack, Image, Box, Text } from '@everlywell/ui-kit';
import React from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { ITestimonialSliderProps } from 'utils/types/index';

export const TestimonialSlider: React.FC<ITestimonialSliderProps> = ({
  testimonials,
}) => (
  <Box
    as="section"
    backgroundColor="#F9F9F6"
    position="relative"
    py={16}
    px={8}
  >
    <Box maxWidth="1440px" mx="auto" width="100%">
      <Swiper
        modules={[Navigation, Pagination]}
        spaceBetween={30}
        slidesPerView={1}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        pagination={{
          clickable: true,
          el: '.swiper-pagination',
          type: 'bullets',
          renderBullet: (index, className) =>
            `<button class="${className}" aria-label="Go to slide ${
              index + 1
            }"></button>`,
        }}
        className="testimonial-swiper"
      >
        {testimonials.map((testimonial, index) => (
          <SwiperSlide key={index}>
            <Stack
              direction={['column', 'row']}
              align="center"
              gap={12}
              style={{ maxWidth: '1200px', margin: '0 auto' }}
            >
              <Box flex="1" mb={{ base: 6, md: 0 }}>
                <Image
                  src={testimonial.image?.file?.url}
                  alt={testimonial.authorName}
                  onError={(e) => {
                    e.currentTarget.src = 'fallback-image-path.jpg';
                  }}
                  loading="lazy"
                />
              </Box>
              <Flex direction="column" flex="1" gap={6}>
                <Text size={['lg', 'xl']} style={{ lineHeight: '140%' }}>
                  "{testimonial.quote?.childMarkdownRemark?.rawMarkdownBody}"
                </Text>

                <div
                  style={{
                    borderColor: '#E8E8E2',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  }}
                />

                <Flex direction="column" gap={1}>
                  <Text size="lg" fontWeight="bold">
                    {testimonial.authorName}
                  </Text>
                  <Text color="#949494">{testimonial.authorTitle}</Text>
                </Flex>
              </Flex>
            </Stack>
          </SwiperSlide>
        ))}
      </Swiper>

      <Flex
        align="center"
        justify="space-between"
        position="relative"
        width="100%"
        style={{ maxWidth: '1200px', margin: '0 auto' }}
        px="1rem"
        py={4}
      >
        <Box
          className="swiper-pagination"
          sx={{
            position: 'relative !important',
            width: 'auto !important',
            '.swiper-pagination-bullet': {
              background: '#012b3b !important',
              opacity: 0.2,
            },
            '.swiper-pagination-bullet-active': {
              opacity: '1 !important',
            },
          }}
        />
        <Flex gap={12} align="center" width="auto">
          <Box
            className="swiper-button-prev"
            sx={{
              position: 'relative !important',
              left: 'auto !important',
              margin: '0 !important',
              cursor: 'pointer',
              '&::after': {
                display: 'none',
              },
            }}
          >
            <LeftArrow />
          </Box>
          <Box
            className="swiper-button-next"
            sx={{
              position: 'relative !important',
              margin: '0 !important',
              cursor: 'pointer',
              '&::after': {
                display: 'none',
              },
            }}
          >
            <RightArrow />
          </Box>
        </Flex>
      </Flex>
    </Box>

    <style>{`
    @media (max-width: 768px) {
      .swiper-pagination {
        width: 100% !important;
      }
      .swiper-button-prev,
      .swiper-button-next {
        display: none !important;
      }
    }
  `}</style>
  </Box>
);

const LeftArrow = () => (
  <svg
    className="swiper-button-prev"
    width={32}
    height={32}
    style={{
      cursor: 'pointer',
      width: '32px',
      height: '32px',
    }}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="button"
    aria-label="Previous slide"
  >
    <rect
      x="0.75"
      y="0.75"
      width="30.5"
      height="30.5"
      rx="15.25"
      stroke="#012B3B"
      strokeWidth="1.5"
    />
    <path
      d="M17 11L12 16L17 21"
      stroke="#012B3B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RightArrow = () => (
  <svg
    className="swiper-button-next"
    width={32}
    height={32}
    style={{
      cursor: 'pointer',
      width: '32px',
      height: '32px',
    }}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="button"
    aria-label="Next slide"
  >
    <rect
      x="0.75"
      y="0.75"
      width="30.5"
      height="30.5"
      rx="15.25"
      stroke="#012B3B"
      strokeWidth="1.5"
    />
    <path
      d="M14 21L19 16L14 11"
      stroke="#012B3B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
